import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { forkJoin } from 'rxjs';
import { EmployeesService } from '../../../employees/employees.service';
import { EmployeeBasicInfo } from '../../../employees/employees.model';
import { DealersService } from '../../../dealers/dealers.service';
import { Dealer } from '../../../dealers/dealers.model';
import { CompanyFactoryService } from '../../../production-inventory/services/company-factory.service';
import { Company } from '../../../production-inventory/production-inventory.model';
import { ProductService } from '../../../products/product.service';
import { ProductNameList } from '../../../products/product.model';
import { emptyOrderEntityFilters, OrderEntityFilters } from '../../orders.models';

type FilterKey = keyof OrderEntityFilters;

/**
 * Product / company / dealer / employee / date-range filter bar shared by the pending-orders
 * and dispatched-orders sections. Both their list and export endpoints take the same param
 * names, so the same bar drives both.
 *
 * Section-specific controls (status select, export button) are projected into the row
 * via ng-content so each section keeps its own.
 */
@Component({
  selector: 'app-order-entity-filters',
  standalone: false,
  templateUrl: './order-entity-filters.component.html',
})
export class OrderEntityFiltersComponent implements OnInit {
  @Input() filters: OrderEntityFilters = emptyOrderEntityFilters();

  /** What the range dates: the order date here, the dispatch date on the dispatched section. */
  @Input() dateLabel: string = 'Order date';

  @Output() filtersChange = new EventEmitter<OrderEntityFilters>();

  protected products: ProductNameList[] = [];
  protected companies: Company[] = [];
  protected dealers: Dealer[] = [];
  protected employees: EmployeeBasicInfo[] = [];

  constructor(
    private productService: ProductService,
    private companyFactoryService: CompanyFactoryService,
    private dealersService: DealersService,
    private employeesService: EmployeesService
  ) {}

  ngOnInit(): void {
    // The `lighter_list` endpoints are this codebase's dropdown sources — they return
    // the full set, unlike the paginated list endpoints behind the same resources.
    forkJoin([
      this.productService.lighter_list(),
      this.companyFactoryService.listCompanies(),
      this.dealersService.lighter_list(),
      this.employeesService.lighter_list({ team: 'sales' }),
    ]).subscribe({
      next: ([products, companies, dealers, employees]) => {
        this.products = products ?? [];
        this.companies = companies ?? [];
        this.dealers = dealers ?? [];
        this.employees = employees ?? [];
      },
    });
  }

  protected get activeFilters(): { key: FilterKey; label: string; value: string }[] {
    const labels: { key: FilterKey; label: string; value: string | null }[] = [
      { key: 'product', label: 'Product', value: this.labelFor(this.products, this.filters.product, 'name') },
      { key: 'company', label: 'Company', value: this.labelFor(this.companies, this.filters.company, 'name') },
      { key: 'dealer', label: 'Dealer', value: this.labelFor(this.dealers, this.filters.dealer, 'shop_name') },
      {
        key: 'employee',
        label: 'Employee',
        value: this.labelFor(this.employees, this.filters.employee, 'full_name'),
      },
      { key: 'from_date', label: `${this.dateLabel} from`, value: this.filters.from_date },
      { key: 'to_date', label: `${this.dateLabel} to`, value: this.filters.to_date },
    ];

    return labels.filter((f): f is { key: FilterKey; label: string; value: string } => f.value !== null);
  }

  /** The API answers a reversed range with a 400, so hold the request and say so here instead. */
  protected get isRangeReversed(): boolean {
    const { from_date, to_date } = this.filters;

    return !!from_date && !!to_date && from_date > to_date;
  }

  protected onFilterChange(): void {
    if (this.isRangeReversed) return;

    this.filtersChange.emit({ ...this.filters });
  }

  protected removeFilter(key: FilterKey): void {
    this.filters = { ...this.filters, [key]: null };
    this.onFilterChange();
  }

  protected clearAll(): void {
    this.filters = emptyOrderEntityFilters();
    this.onFilterChange();
  }

  /** Null when the filter is unset, so an unset filter never becomes a chip. */
  private labelFor(items: any[], id: string | null, labelField: string): string | null {
    if (!id) return null;

    return items.find(item => item.id === id)?.[labelField] ?? '—';
  }
}
